/**
 * @file components/NavItem/component.js
 * @copyright 2017 CBC/Radio-Canada. All rights reserved.
 * @author Fitore Jaha [fitore.jaha@cbc.ca]
 */

import PropTypes from "prop-types";
import classNames from "classnames";
import LinkWrap from "../LinkWrap/component";
import {
    normalizeClass,
    makeAbsoluteCBCDomain,
} from "../../utils/path/library";

/**
 * The NavItem component.
 *
 * This component renders a single navigation list item with a link or a span, depending if it&#39;s selected
 *
 * @param {Object} props The component props
 * @returns {JSX.Element} Returns the NavItem component
 */
export function NavItem({
    item,
    index = "",
    className,
    linkClassName,
    path = "",
    clickHandler = () => {},
    isExternalHeader = false,
    toggleMenuVisibility = () => {},
    onBlur,
    keyUpHandler,
    ariaLabel,
}) {
    // Link can be internal or external, so use appropriate markup
    const currentLink = item.link;
    const linkType = item.type;
    const linkTitle = item.title;
    const linkKey = `link-${index}`;
    const isExternal =
        (linkType !== undefined && linkType === "external") || isExternalHeader;
    const isSelected = currentLink === `/${path}`;
    let itemMarkup = null;

    const listItemClasses = classNames("navItem", className, {
        standAlone: item.standAlone !== undefined,
    });

    const ariaLabelProps = ariaLabel
        ? { "aria-label": `${linkTitle} ${ariaLabel}` }
        : {};

    const linkClasses = classNames(
        "navLink",
        linkClassName,
        normalizeClass("nav", linkTitle),
        {
            selected: isSelected,
        }
    );

    const linkDataAttributes = item.data === undefined ? {} : item.data;
    const navItemDataCy = `navItem${linkTitle}`;

    // If a nav item is selected, it shouldn't be a link anymore
    if (isSelected) {
        itemMarkup = (
            <span
                className={linkClasses}
                key={linkKey}
                {...ariaLabelProps}
                data-cy={navItemDataCy}
            >
                <span className="a11y">Currently Selected: {linkTitle}</span>
                <span aria-hidden="true">{linkTitle}</span>
            </span>
        );
    } else if (linkType === "asset") {
        // Case for a navitem to simply link to an asset, to display/download. ie: download holiday schedule pdf
        itemMarkup = (
            <a
                className={linkClasses}
                key={linkKey}
                href={currentLink}
                target="_blank"
                rel="noopener noreferrer"
                onKeyUp={keyUpHandler}
                {...ariaLabelProps}
                data-cy={navItemDataCy}
            >
                {linkTitle}
            </a>
        );
    } else {
        const closeMenuFunction = toggleMenuVisibility;
        const navItemClickHandler = item.clickHandler || clickHandler;
        const linkURL = isExternalHeader
            ? makeAbsoluteCBCDomain(item.link)
            : item.link;
        const linkProps = {
            className: linkClasses,
            key: linkKey,
            url: linkURL,
            external: isExternal,
            onClick: (e) => {
                closeMenuFunction();
                navItemClickHandler(e);
            },
            onKeyUp: keyUpHandler,
            "data-cy": navItemDataCy,
        };
        if (onBlur) {
            linkProps.onBlur = onBlur;
        }

        itemMarkup = (
            <LinkWrap
                {...linkProps}
                {...linkDataAttributes}
                {...ariaLabelProps}
            >
                {linkTitle}
            </LinkWrap>
        );
    }

    return (
        <li
            key={`li-${linkTitle}-${index}`}
            className={listItemClasses}
            data-testid="nav-item"
            data-cy="nav-item"
        >
            {itemMarkup}
        </li>
    );
}

NavItem.propTypes = {
    item: PropTypes.object.isRequired,
    index: PropTypes.node,
    className: PropTypes.string,
    linkClassName: PropTypes.string,
    path: PropTypes.string,
    clickHandler: PropTypes.func,
    isExternalHeader: PropTypes.bool,
    toggleMenuVisibility: PropTypes.func,
    onBlur: PropTypes.func,
};

export default NavItem;
